import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import {
  Building2Icon,
  CopyIcon,
  MoreVerticalIcon,
  PencilIcon,
  PowerIcon,
  PowerOffIcon,
  Trash2Icon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import { BriefTemplateConfirmDialog } from './BriefTemplateConfirmDialog';
import type { BriefTemplatesActionMenuProps } from './BriefTemplatesActionMenu.types';
import { useBriefTemplatesActionMenu } from './hooks/useBriefTemplatesActionMenu';

/**
 * Row action menu for a Brief Template.
 *
 * Every item is a write the caller must be allowed to make on this row, so the trigger goes
 * when none is, rather than opening a menu whose every item the API would refuse.
 *
 * The trigger stops click propagation so the surrounding row's navigation
 * handler does NOT fire when the user opens the menu.
 *
 * Keyboard a11y: the trigger is a focusable `<Button>`, the menu is
 * Radix-backed via monks-ui — Tab/Enter/Space open it, Escape closes it
 * and returns focus to the trigger.
 */
export function BriefTemplatesActionMenu(props: BriefTemplatesActionMenuProps) {
  const { t } = useTranslation('knowledge');
  const { t: tCommon } = useTranslation('common');
  const menu = useBriefTemplatesActionMenu(props);
  const { actions, handlers } = menu;

  if (!menu.hasActions) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="mini"
            aria-label={t('knowledgeHub.briefTemplatesActionMenu.briefTemplateActions')}
            data-template-id={props.templateId}
            className="size-9 p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVerticalIcon className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          {actions.canEdit && (
            <DropdownMenuItem onSelect={handlers.edit}>
              <PencilIcon className="mr-2 size-4" />
              {t('knowledgeHub.briefTemplatesActionMenu.edit')}
            </DropdownMenuItem>
          )}
          {actions.canDuplicate && (
            <DropdownMenuItem onSelect={handlers.duplicate} disabled={menu.isDuplicating}>
              <CopyIcon className="mr-2 size-4" />
              {t('knowledgeHub.briefTemplatesActionMenu.duplicate')}
            </DropdownMenuItem>
          )}
          {actions.canPromote && (
            <DropdownMenuItem onSelect={handlers.openPromote}>
              <Building2Icon className="mr-2 size-4" />
              {t('knowledgeHub.briefTemplatesActionMenu.promoteToOrganization')}
            </DropdownMenuItem>
          )}
          {actions.canToggle && (
            <DropdownMenuItem onSelect={handlers.toggle} disabled={menu.isToggling}>
              {menu.enabled ? (
                <PowerOffIcon className="mr-2 size-4" />
              ) : (
                <PowerIcon className="mr-2 size-4" />
              )}
              {menu.enabled
                ? t('knowledgeHub.briefTemplatesActionMenu.turnOff')
                : t('knowledgeHub.briefTemplatesActionMenu.turnOn')}
            </DropdownMenuItem>
          )}
          {actions.canDelete && (
            <DropdownMenuItem
              onSelect={handlers.openDelete}
              className="text-destructive focus:text-destructive"
            >
              <Trash2Icon className="mr-2 size-4" />
              {t('knowledgeHub.briefTemplatesActionMenu.delete')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {/* Radix Dialog portals to body, but React SyntheticEvents bubble
          through the React tree — without this wrapper, a confirm-button
          click reaches the parent <TableRow onClick> and navigates. The
          wrapper is structural; keyboard interaction lives on the dialogs
          themselves (Esc closes, Enter confirms via the focused button). */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div onClick={(e) => e.stopPropagation()}>
        <DeleteConfirmationModal
          open={menu.isDeleteOpen}
          isDeleting={menu.isDeleting}
          onCancel={handlers.cancelDelete}
          onConfirmDelete={handlers.confirmDelete}
          resourceType={tCommon('components.deleteConfirmationModal.template')}
          resourceName={props.templateName}
          warningMessage={t('knowledgeHub.briefTemplatesActionMenu.thisActionCannotBeUndone')}
          data-testid="brief-template-delete-modal"
        />
        <BriefTemplateConfirmDialog
          open={menu.isPromoteOpen}
          title={t('knowledgeHub.briefTemplatePromoteDialog.title')}
          description={t('knowledgeHub.briefTemplatePromoteDialog.description', {
            name: props.templateName,
          })}
          confirmLabel={t('knowledgeHub.briefTemplatePromoteDialog.promote')}
          cancelLabel={t('knowledgeHub.briefTemplatePromoteDialog.cancel')}
          isPending={menu.isPromoting}
          onCancel={handlers.cancelPromote}
          onConfirm={handlers.confirmPromote}
        />
        <BriefTemplateConfirmDialog
          open={menu.isTurnOffOpen}
          title={t('knowledgeHub.briefTemplateTurnOffDialog.title')}
          description={t('knowledgeHub.briefTemplateTurnOffDialog.description', {
            name: props.templateName,
          })}
          confirmLabel={t('knowledgeHub.briefTemplateTurnOffDialog.turnOff')}
          cancelLabel={t('knowledgeHub.briefTemplateTurnOffDialog.cancel')}
          isPending={menu.isToggling}
          onCancel={handlers.cancelTurnOff}
          onConfirm={handlers.confirmTurnOff}
        />
      </div>
    </>
  );
}
